import type { SelectParam } from '../types';
import { DEG2RAD, TAU, clamp01, mod, smoothstep } from '../math/scalar';

/**
 * Building blocks shared by the pattern library.
 *
 * The profile functions matter well beyond looks: a `triangle` or `round`
 * profile prints without support because every surface stays under the
 * overhang limit, while `step` produces flat plateaus that need bridging. Giving
 * every ridge-like pattern the same profile vocabulary keeps that choice
 * consistent across the whole texture pack.
 */

export type WaveProfile = 'sine' | 'triangle' | 'round' | 'cove' | 'step' | 'saw';

export const PROFILE_OPTIONS: SelectParam['options'] = [
  { value: 'sine', label: 'Sine — soft and continuous' },
  { value: 'triangle', label: 'Triangle — faceted, support-free' },
  { value: 'round', label: 'Round — bullnose' },
  { value: 'cove', label: 'Cove — concave channel' },
  { value: 'step', label: 'Step — flat plateaus' },
  { value: 'saw', label: 'Saw — asymmetric ramp' },
];

/**
 * Maps a phase in `0 .. 1` to a height in `0 .. 1`. Every profile starts and
 * ends at `0` so that repeating it produces a continuous field.
 */
export function waveProfile(phase: number, profile: WaveProfile, duty = 0.5): number {
  const t = mod(phase, 1);
  switch (profile) {
    case 'sine':
      return 0.5 - 0.5 * Math.cos(t * TAU);
    case 'triangle':
      return 1 - Math.abs(2 * t - 1);
    case 'round': {
      const d = 2 * t - 1;
      return Math.sqrt(Math.max(0, 1 - d * d));
    }
    case 'cove': {
      const d = 2 * t - 1;
      return 1 - Math.sqrt(Math.max(0, 1 - d * d));
    }
    case 'step': {
      const width = clamp01(duty);
      // Soften the vertical walls by a fraction of the period so the mesh keeps
      // a printable slope instead of a zero-width cliff.
      const edge = 0.04;
      const rise = smoothstep(0, edge, t);
      const fall = 1 - smoothstep(width, width + edge, t);
      return Math.min(rise, fall);
    }
    case 'saw':
      return t;
  }
}

/** Projects a wall-space point onto a direction, returning the distance along it. */
export function axisDistance(x: number, y: number, angleDegrees: number): number {
  const angle = angleDegrees * DEG2RAD;
  return x * Math.cos(angle) + y * Math.sin(angle);
}

/** Projects a wall-space point onto the axis perpendicular to `angleDegrees`. */
export function crossAxisDistance(x: number, y: number, angleDegrees: number): number {
  const angle = angleDegrees * DEG2RAD;
  return -x * Math.sin(angle) + y * Math.cos(angle);
}

/**
 * Distance to the centre of the enclosing cell of size `cell`, normalised so
 * that `0` is the cell centre and `1` is the middle of a cell edge.
 */
export function cellCoordinate(value: number, cell: number): { index: number; local: number } {
  const index = Math.floor(value / cell);
  return { index, local: value / cell - index };
}

/** Chebyshev (square) distance from the centre of a unit cell. */
export function squareRadius(u: number, v: number): number {
  return Math.max(Math.abs(u - 0.5), Math.abs(v - 0.5)) * 2;
}

/** Manhattan (diamond) distance from the centre of a unit cell. */
export function diamondRadius(u: number, v: number): number {
  return (Math.abs(u - 0.5) + Math.abs(v - 0.5)) * 2;
}

/** Euclidean distance from the centre of a unit cell, normalised to the edge. */
export function circleRadius(u: number, v: number): number {
  const du = (u - 0.5) * 2;
  const dv = (v - 0.5) * 2;
  return Math.sqrt(du * du + dv * dv);
}

/**
 * Panel pitch measured along an arbitrary direction.
 *
 * Span parameters are expressed in panels, so a rotated motif needs to know how
 * far apart the panel joints are along *its* axis. Square grids have equal
 * pitches and this collapses to a constant, but non-square shapes (Phase 2) do
 * not, which is why the blend lives here rather than being hard-coded.
 */
export function pitchAlongAxis(pitchX: number, pitchY: number, angleDegrees: number): number {
  const angle = angleDegrees * DEG2RAD;
  return Math.abs(Math.cos(angle)) * pitchX + Math.abs(Math.sin(angle)) * pitchY;
}

/** Rotates a wall-space point around the origin. */
export function rotatePoint(x: number, y: number, angleDegrees: number): { x: number; y: number } {
  if (angleDegrees === 0) return { x, y };
  const angle = angleDegrees * DEG2RAD;
  const cos = Math.cos(angle);
  const sin = Math.sin(angle);
  return { x: x * cos + y * sin, y: -x * sin + y * cos };
}

/**
 * Ramps a normalised cell coordinate down to zero within `bevel` of either
 * edge. Used to give tiles a printable chamfer instead of a vertical wall.
 */
export function bevelRamp(t: number, bevel: number): number {
  if (bevel <= 0) return t >= 0 && t <= 1 ? 1 : 0;
  const width = Math.min(bevel, 0.5);
  return Math.min(smoothstep(0, width, t), smoothstep(0, width, 1 - t));
}
